/**
 * Shared modal that frames privilege-certification status with a counts row and
 * progress bar. Used both as a campaign-creation blocker (error variant) and as
 * an in-context work queue on the Privilege Mapping screen (work-queue variant).
 *
 * The chrome (header, counts, progress) is fixed; the body and footer are slots
 * so each caller can render its own action surface (next-steps text vs. the
 * uncertified-items table with Certify buttons).
 */

import React from 'react';
import WideModal from './WideModal';

const VARIANT_CONFIG = {
  error: {
    headerClass: 'bg-danger text-white',
    headerIcon: 'fa-exclamation-triangle',
    alertClass: 'alert-danger',
    alertHeading: 'Privilege Certification Required',
  },
  'work-queue': {
    headerClass: 'bg-warning text-dark',
    headerIcon: 'fa-clipboard-check',
    alertClass: 'alert-warning',
    alertHeading: 'Items Awaiting Certification',
  },
};

const CertificationStatusModal = ({
  show,
  onClose,
  variant = 'error',
  title,
  databaseType,
  totalCount = 0,
  certifiedCount = 0,
  uncertifiedCount = 0,
  alertMessage,
  children,
  footer,
}) => {
  const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.error;
  const certifiedPct = totalCount > 0
    ? Math.round((certifiedCount / totalCount) * 100)
    : 0;

  return (
    <WideModal
      show={show}
      onClose={onClose}
      title={title}
      headerIcon={config.headerIcon}
      headerClass={config.headerClass}
      maxWidth="1200px"
      footer={footer}
    >
      {alertMessage && (
        <div className={`alert ${config.alertClass} mb-4`} style={{ border: 'none' }}>
          <div className="d-flex align-items-start">
            <i className="fas fa-exclamation-circle fa-2x me-3 mt-1"></i>
            <div>
              <h6 className="alert-heading mb-2">{config.alertHeading}</h6>
              <p className="mb-0">{alertMessage}</p>
            </div>
          </div>
        </div>
      )}

      <div className="card mb-4" style={{ borderColor: '#ffc107' }}>
        <div className="card-header" style={{ backgroundColor: '#fff3cd', borderColor: '#ffc107' }}>
          <h6 className="mb-0">
            <i className="fas fa-chart-bar me-2"></i>
            Certification Status{databaseType ? ` — ${databaseType}` : ''}
          </h6>
        </div>
        <div className="card-body">
          <div className="row text-center">
            <div className="col-4">
              <div className="mb-2">
                <i className="fas fa-list fa-2x text-secondary"></i>
              </div>
              <h4 className="mb-1">{totalCount}</h4>
              <small className="text-muted">Total</small>
            </div>
            <div className="col-4">
              <div className="mb-2">
                <i className="fas fa-check-circle fa-2x text-success"></i>
              </div>
              <h4 className="mb-1">{certifiedCount}</h4>
              <small className="text-muted">Certified</small>
            </div>
            <div className="col-4">
              <div className="mb-2">
                <i className="fas fa-times-circle fa-2x text-danger"></i>
              </div>
              <h4 className="mb-1">{uncertifiedCount}</h4>
              <small className="text-muted">Uncertified</small>
            </div>
          </div>
          <div className="progress mt-3" style={{ height: '25px' }}>
            <div
              className="progress-bar bg-success"
              role="progressbar"
              style={{ width: `${certifiedPct}%` }}
            >
              {certifiedPct}%
            </div>
          </div>
        </div>
      </div>

      {children}
    </WideModal>
  );
};

export default CertificationStatusModal;
